import type { MaterialsConfig } from '../pricing';
import type { PriceControls } from '../stats/cabinetStats';
import type { Cabinet } from './cabinet';
import type { OptimizationSheet, SheetSize } from './optimization';
import type { CsvPiece, Part } from './parts';
import type { ProjectPrices } from './project';

/** Supported locales for PDF/CSV/report generation. */
export type ReportLocale = 'pt-BR' | 'en-US' | 'es-ES';

export const normalizeReportLocale = (locale: string): ReportLocale => {
  if (locale === 'pt-BR' || locale === 'en-US' || locale === 'es-ES') return locale;
  if (locale.startsWith('en')) return 'en-US';
  if (locale.startsWith('es')) return 'es-ES';
  return 'pt-BR';
};

/**
 * Canonical column definitions for CSV and future Excel exports.
 * Headers are in Portuguese (primary market).
 */
export const CSV_COLUMNS = [
  { key: 'name', header: 'Nome' },
  { key: 'largura', header: 'Largura (mm)' },
  { key: 'altura', header: 'Altura (mm)' },
  { key: 'espessura', header: 'Espessura (mm)' },
  { key: 'quantidade', header: 'Quantidade' },
  { key: 'material', header: 'Material' },
  { key: 'edgeBanding', header: 'Fita de Borda' },
] as const;

export type CsvColumnKey = (typeof CSV_COLUMNS)[number]['key'];

export const CSV_HEADERS = CSV_COLUMNS.map((col) => col.header);

/**
 * Unified row shape for cut-list reports (CSV, Excel, PDF tables).
 * Uses explicit mm fields so all export formats share one contract.
 */
export type PartsReportRow = {
  name: string;
  widthMm: number;
  heightMm: number;
  thicknessMm: number;
  quantity: number;
  material: string;
  edgeBanding: string;
};

export const csvPieceToReportRow = (piece: CsvPiece): PartsReportRow => ({
  name: piece.name,
  widthMm: piece.largura,
  heightMm: piece.altura,
  thicknessMm: piece.espessura,
  quantity: piece.quantidade,
  material: piece.material,
  edgeBanding: piece.edgeBanding,
});

export const reportRowToCsvPiece = (row: PartsReportRow): CsvPiece => ({
  name: row.name,
  largura: row.widthMm,
  altura: row.heightMm,
  espessura: row.thicknessMm,
  quantidade: row.quantity,
  material: row.material,
  edgeBanding: row.edgeBanding,
});

/** Convert a BOM part to a report row when it has cut dimensions. */
export const partToReportRow = (part: Part): PartsReportRow | null => {
  if (part.type !== 'mdf' || part.width == null || part.height == null || part.thickness == null) {
    return null;
  }

  return {
    name: part.name,
    widthMm: part.width,
    heightMm: part.height,
    thicknessMm: part.thickness,
    quantity: part.quantity,
    material: part.material ?? 'MDF',
    edgeBanding: '',
  };
};

/** Input contract for project budget/parts PDF generation. */
export type ProjectReportInput = {
  cabinets: Cabinet[];
  prices: ProjectPrices;
  locale?: ReportLocale;
  currency?: string;
  materialsConfig?: MaterialsConfig | null;
};

/** Input contract for cut optimization PDF/HTML reports. */
export type OptimizationReportInput = {
  sheets: OptimizationSheet[];
  sheetSize: SheetSize;
  locale?: ReportLocale;
};

/** Re-export PriceControls alias used in project reports. */
export type { PriceControls };
